import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Category } from "~/api/model";
import {
  getListOrganizationCategoriesQueryKey,
  getListOrganizationCategoryFoldersQueryKey,
  useUpdateOrganizationCategory,
} from "~/api/organization/organization";
import { AutoResizeTextarea } from "~/components/ui/auto-resize-textarea";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";

interface EditCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: Category;
}

const EditCategoryModal: FC<EditCategoryModalProps> = ({
  open,
  onOpenChange,
  category,
}) => {
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: updateCategory } = useUpdateOrganizationCategory();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: category.name,
    definition: category.definition || "",
    namingConvention: category.namingConvention || "",
    namingExample: category.namingExample || "",
    expectedContent: category.expectedContent || "",
    keyTopics: category.keyTopics || "",
    length: category.length || "",
    recognitionTips: category.recognitionTips || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const categoriesQueryKey =
      getListOrganizationCategoriesQueryKey(organizationId);
    const foldersQueryKey =
      getListOrganizationCategoryFoldersQueryKey(organizationId);

    // Save previous data for rollback
    const previousCategoriesData =
      queryClient.getQueryData<Category[]>(categoriesQueryKey);

    // Optimistic update: immediately update cache with form data
    const optimisticCategory: Category = {
      ...category,
      name: formData.name,
      definition: formData.definition,
      namingConvention: formData.namingConvention,
      namingExample: formData.namingExample,
      expectedContent: formData.expectedContent,
      keyTopics: formData.keyTopics,
      length: formData.length,
      recognitionTips: formData.recognitionTips,
    };

    queryClient.setQueryData(
      categoriesQueryKey,
      (oldData: Category[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map((c) =>
          c.id === category.id ? optimisticCategory : c
        );
      }
    );

    // Close modal immediately
    onOpenChange(false);

    // Update in background with success invalidation and error rollback
    updateCategory({
      organizationId,
      categoryId: category.id.toString(),
      data: {
        name: formData.name,
        definition: formData.definition,
        color: category.color,
        isFallback: category.isFallback,
        namingConvention: formData.namingConvention,
        namingExample: formData.namingExample,
        expectedContent: formData.expectedContent,
        keyTopics: formData.keyTopics,
        length: formData.length,
        recognitionTips: formData.recognitionTips,
      },
    })
      .then(() => {
        // Invalidate both categories and folders queries to sync with server data
        queryClient.invalidateQueries({ queryKey: categoriesQueryKey });
        queryClient.invalidateQueries({ queryKey: foldersQueryKey });
      })
      .catch(() => {
        queryClient.setQueryData(categoriesQueryKey, previousCategoriesData);
        toast({
          variant: "destructive",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: "categorySettings.updateError",
          }),
        });
      });
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  useEffect(() => {
    if (open) {
      setFormData({
        name: category.name,
        definition: category.definition || "",
        namingConvention: category.namingConvention || "",
        namingExample: category.namingExample || "",
        expectedContent: category.expectedContent || "",
        keyTopics: category.keyTopics || "",
        length: category.length || "",
        recognitionTips: category.recognitionTips || "",
      });
    }
  }, [open, category]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={intl.formatMessage({ id: "categorySettings.title" })}
      description={intl.formatMessage({ id: "categorySettings.description" })}
      content={
        <form onSubmit={handleSubmit} id="edit-category-form">
          <fieldset className="space-y-4 pb-4">
            <div className="space-y-1">
              <Label htmlFor="name">
                {intl.formatMessage({ id: "categorySettings.name" })}
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "categorySettings.namePlaceholder",
                })}
                className="shadow-none"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="definition">
                {intl.formatMessage({ id: "categorySettings.definition" })}
              </Label>
              <AutoResizeTextarea
                id="definition"
                value={formData.definition}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData({ ...formData, definition: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "categorySettings.definitionPlaceholder",
                })}
                className="shadow-none"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="naming-conventions">
                {intl.formatMessage({
                  id: "categorySettings.namingConvention",
                })}
              </Label>
              <Input
                id="naming-conventions"
                value={formData.namingConvention}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    namingConvention: e.target.value,
                  })
                }
                placeholder={intl.formatMessage({
                  id: "categorySettings.namingConventionPlaceholder",
                })}
                className="shadow-none"
              />
              {!formData.namingConvention && (
                <p className="text-sm text-muted-foreground">
                  {intl.formatMessage({
                    id: "categorySettings.namingConventionHint",
                  })}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="examples">
                {intl.formatMessage({ id: "categorySettings.examples" })}
              </Label>
              <AutoResizeTextarea
                id="naming-example"
                value={formData.namingExample}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData({
                    ...formData,
                    namingExample: e.target.value,
                  })
                }
                placeholder={intl.formatMessage({
                  id: "categorySettings.examplesPlaceholder",
                })}
                className="shadow-none"
              />
              <p className="text-sm text-muted-foreground">
                {intl.formatMessage({ id: "categorySettings.examplesHint" })}
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="expected-content">
                {intl.formatMessage({
                  id: "categorySettings.expectedContent",
                })}
              </Label>
              <AutoResizeTextarea
                id="expected-content"
                value={formData.expectedContent}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData({
                    ...formData,
                    expectedContent: e.target.value,
                  })
                }
                placeholder={intl.formatMessage({
                  id: "categorySettings.expectedContentPlaceholder",
                })}
                className="shadow-none"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="key-topics">
                {intl.formatMessage({ id: "categorySettings.keyTopics" })}
              </Label>
              <AutoResizeTextarea
                id="key-topics"
                value={formData.keyTopics}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData({ ...formData, keyTopics: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "categorySettings.keyTopicsPlaceholder",
                })}
                className="shadow-none"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="recognition-tips">
                {intl.formatMessage({
                  id: "categorySettings.recognitionTips",
                })}
              </Label>
              <AutoResizeTextarea
                id="recognition-tips"
                value={formData.recognitionTips}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData({
                    ...formData,
                    recognitionTips: e.target.value,
                  })
                }
                placeholder={intl.formatMessage({
                  id: "categorySettings.recognitionTipsPlaceholder",
                })}
                className="shadow-none"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="length">
                {intl.formatMessage({ id: "categorySettings.length" })}
              </Label>
              <Input
                id="length"
                value={formData.length}
                onChange={(e) =>
                  setFormData({ ...formData, length: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "categorySettings.lengthPlaceholder",
                })}
                className="shadow-none"
              />
            </div>
          </fieldset>
        </form>
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={handleCancel}>
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          <Button type="submit" form="edit-category-form">
            {intl.formatMessage({ id: "save" })}
          </Button>
        </>
      }
    />
  );
};

export default EditCategoryModal;
